import type { FiltersConfig } from '@openx/types/chipFilters';
import type { TFunction } from 'i18next';

export type GetFiltersConfigProps = {
  accountsLoading: boolean;
  providersLoading: boolean;
  statusesLoading: boolean;
  metadataLoading: boolean;
  regionsLoading: boolean;
  countriesLoading: boolean;
  accounts: Array<{ name: string; id: string }>;
  providers: Array<{ name: string }>;
  statuses: Array<{ displayName: string; status: string }>;
  regions: Array<{ region_code: string }>;
  countries: Array<{ country_code: string }>;
  categories: Array<string>;
  subCategories: Array<string>;
  types: Array<string>;
  t: TFunction;
};

const SEPARATOR = '; ';

export const getFiltersConfig = ({
  accounts,
  accountsLoading,
  providers,
  providersLoading,
  statuses,
  statusesLoading,
  t,
  metadataLoading,
  regions,
  countries,
  regionsLoading,
  countriesLoading,
  categories,
  subCategories,
  types,
}: GetFiltersConfigProps): FiltersConfig => {
  return {
    account: {
      loading: accountsLoading,
      multiple: true,
      name: t('Data Partner'),
      options: accounts.map(({ name }) => ({ displayName: name, value: encodeURIComponent(name) })),
      separator: SEPARATOR,
    },
    provider: {
      loading: providersLoading,
      multiple: true,
      name: t('Data Provider'),
      options: providers.map(({ name }) => ({ displayName: name, value: encodeURIComponent(name) })),
      separator: SEPARATOR,
    },
    status: {
      loading: statusesLoading,
      multiple: true,
      name: t('Status'),
      options: statuses.map(item => ({ displayName: item.displayName, value: item.status })),
    },
    regions: {
      loading: regionsLoading,
      multiple: true,
      name: t('Region'),
      options: regions.map(({ region_code }) => ({
        displayName: region_code,
        value: encodeURIComponent(region_code),
      })),
      separator: SEPARATOR,
    },
    countries: {
      loading: countriesLoading,
      multiple: true,
      name: t('Country'),
      options: countries.map(({ country_code }) => ({
        displayName: country_code,
        value: encodeURIComponent(country_code),
      })),
      separator: SEPARATOR,
    },
    reach: {
      loading: false,
      multiple: false,
      name: t('Reach'),
      numberFilter: {
        min: 0,
        max: 1000000000,
        maxFractionDigits: 0,
      },
    },
    segment_category: {
      loading: metadataLoading,
      multiple: true,
      name: t('Category'),
      options: categories.map(item => ({ displayName: item, value: encodeURIComponent(item) })),
      separator: SEPARATOR,
    },
    segment_sub_category: {
      loading: metadataLoading,
      multiple: true,
      name: t('Sub Category'),
      options: subCategories.map(item => ({ displayName: item, value: encodeURIComponent(item) })),
      separator: SEPARATOR,
    },
    segment_type: {
      loading: metadataLoading,
      multiple: true,
      name: t('Type'),
      options: types.map(item => ({ displayName: item, value: encodeURIComponent(item) })),
      separator: SEPARATOR,
    },
    price_pom: {
      loading: false,
      multiple: false,
      name: t('Percent of Media'),
      numberFilter: {
        min: 0,
        max: 100,
        maxFractionDigits: 2,
        currencySymbol: '%',
      },
    },
    price_cpm: {
      loading: false,
      multiple: false,
      name: t('Fixed CPM'),
      numberFilter: {
        min: 0,
        max: 1000000000,
        maxFractionDigits: 0,
        currencySymbol: '$',
      },
    },
    updated_date: {
      loading: false,
      multiple: false,
      name: t('Last Modified'),
      datePicker: {
        min: new Date('2020-01-01'),
        max: new Date(),
      },
    },
    created_date: {
      loading: false,
      multiple: false,
      name: t('Created'),
      datePicker: {
        min: new Date('2020-01-01'),
        max: new Date(),
      },
    },
  };
};
